import { useSetAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { editPointOfInterest as editPointOfInterestAtom } from '../state';
import {
  firstSelectedSphericalIdForVoi,
  viewerCurrentSpherical as viewerCurrentSphericalAtom,
} from '@/components/Analysis/state';
import { PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';

/**
 * Custom hook to track and store the first spherical ID selected when a VOI (Volume of Interest)
 * creation flow is initiated from a spherical view. It ensures the original spherical context is preserved even if
 * the user navigates to different sphericals during the creation process to adjust the VOI sizing.
 * Jira: https://abyss-solutions.atlassian.net/browse/WSM-411
 */
export const useVoiFirstSphericalTracking = () => {
  const setFirstSpherical = useSetAtom(firstSelectedSphericalIdForVoi);
  const editPoi = useAtomValue(editPointOfInterestAtom);
  const viewerCurrentSpherical = useAtomValue(viewerCurrentSphericalAtom);

  useEffect(() => {
    const isVoiFlow =
      editPoi.state !== 'Inactive' &&
      editPoi.template?.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest;

    if (!isVoiFlow) {
      // VOI flow ended → clear the first spherical
      setFirstSpherical(undefined);
      return;
    }

    // VOI flow is active
    // Capture the current spherical ONLY if we haven't captured one yet
    setFirstSpherical((prev) => {
      // If we already have a first spherical, keep it
      if (prev) return prev;
      // Otherwise, capture the current one
      return viewerCurrentSpherical?.id ?? undefined;
    });
  }, [editPoi.state, editPoi.template?.type, viewerCurrentSpherical?.id, setFirstSpherical]);
};
